import { useMemo } from "react";
import L from "leaflet";
import {
  haversineDistance,
  generateCurve,
} from "../utils/geoUtils";

export const useMapTracking = ({
  orderDetails,
  driverPosition: finalDriverPosition,
  merchantStatus,
  driverStatus,
  driverPhase,
  assets,
}) => {
  const pickupLat = Number(orderDetails?.merchant_latitude);
  const pickupLng = Number(orderDetails?.merchant_longitude);

  const homeLat = Number(orderDetails?.customer_latitude);
  const homeLng = Number(orderDetails?.customer_longitude);

  const driverLat = finalDriverPosition?.[0];
const driverLng = finalDriverPosition?.[1];

  const hasDriverLocation =
  Number.isFinite(driverLat) && Number.isFinite(driverLng);

  /* ICON CREATOR  */

const createWhiteIcon = (iconUrl) =>
  L.divIcon({
    className: "custom-marker",
    html: `
      <div style="
        width:40px;
        height:40px;
        background:#fff7ed;
        border:2px solid #fb923c;
        border-radius:50%;
        display:flex;
        align-items:center;
        justify-content:center;
        // box-shadow:0 2px 12px rgba(251,146,60,0.25);
      ">
        <img src="${iconUrl}" style="width:22px;height:22px;" />
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });

  const restaurantIcon = useMemo(
    () => createWhiteIcon(assets.restaurant),
    [assets.restaurant]
  );

  const homeIcon = useMemo(
    () => createWhiteIcon(assets.home),
    [assets.home]
  );

  const driverIcon = useMemo(
    () => createWhiteIcon(assets.driver),
    [assets.driver]
  );

  /*  MAP BOUNDS  */

  const bounds = useMemo(() => {
    const b = [];
    if (pickupLat && pickupLng) b.push([pickupLat, pickupLng]);
    if (homeLat && homeLng) b.push([homeLat, homeLng]);
    if (hasDriverLocation) b.push([driverLat, driverLng]);
    return b;
  }, [pickupLat, pickupLng, homeLat, homeLng, driverLat, driverLng, hasDriverLocation]);

  /* DUMMY DRIVER  */

  const dummyDriverPosition = useMemo(() => {
    if (!pickupLat || !pickupLng) return null;
    return [pickupLat + 0.002, pickupLng + 0.002];
  }, [pickupLat, pickupLng]);

  const showDummyDriver = !hasDriverLocation && dummyDriverPosition;

  const dummyRoute = useMemo(() => {
    if (!dummyDriverPosition || !pickupLat || !pickupLng) return [];
    return generateCurve(dummyDriverPosition, [pickupLat, pickupLng]);
  }, [dummyDriverPosition, pickupLat, pickupLng]);

  /* REAL ROUTE  */

const realRoute = useMemo(() => {
  if (!hasDriverLocation) return [];

  //  Driver waiting / restaurant phase
  if (
    ["NOT_STARTED", "AT_RESTAURANT", "ASSIGNED", "PREPARING"]
      .includes(driverPhase)
  ) {
    if (!pickupLat || !pickupLng) return [];
    return generateCurve(
      [driverLat, driverLng],
      [pickupLat, pickupLng]
    );
  }

  //  Driver on the way to home
  if (driverPhase === "ON_THE_WAY") {
    if (!homeLat || !homeLng) return [];
    return generateCurve(
      [driverLat, driverLng],
      [homeLat, homeLng]
    );
  }

  return [];
}, [
  driverPhase,
  hasDriverLocation,
  driverLat,
  driverLng,
  pickupLat,
  pickupLng,
  homeLat,
  homeLng,
]);

  /*  STATUS HELPERS  */

  const isFoodReady = merchantStatus === "food is ready";

  const distanceKm =
    hasDriverLocation && homeLat && homeLng
      ? haversineDistance(driverLat, driverLng, homeLat, homeLng)
      : null;

  const formattedStoreDistance = distanceKm
    ? distanceKm < 1
      ? `${Math.round(distanceKm * 1000)} m`
      : `${distanceKm.toFixed(1)} km`
    : null;

  return {
    pickupLat,
    pickupLng,
    homeLat,
    homeLng,
    hasDriverLocation,
    bounds,

    restaurantIcon,
    homeIcon,
    driverIcon,

    showDummyDriver,
    dummyDriverPosition,
    dummyRoute,
    realRoute,
    distanceKm,  
    isFoodReady,
    formattedStoreDistance,
  };
};
